import { useCallback, useEffect, useRef, useState } from 'react';
import { getResult, getStatus } from '@/lib/api';

const TERMINAL_STATES = new Set(['ready', 'failed']);
const POLL_INTERVAL_MS = 1500;

/**
 * useAnalysisJob - polls the analyzer backend for a given job_id and
 * exposes { status, result, error, startPolling, stopPolling, resetJob }.
 * Extracted from AnalyzerPage to reduce complexity and separate concerns.
 */
export function useAnalysisJob() {
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (id) => {
      if (!id) return;
      stopPolling();
      pollingRef.current = setInterval(async () => {
        try {
          const s = await getStatus(id);
          setStatus(s);
          if (s.status === 'ready') {
            stopPolling();
            const r = await getResult(id);
            setResult(r);
          } else if (s.status === 'failed') {
            stopPolling();
            setError(s.error || 'Analysis failed');
          }
        } catch {
          // network glitch — leave polling active, next tick will retry
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling]
  );

  const resetJob = useCallback(() => {
    stopPolling();
    setStatus(null);
    setResult(null);
    setError(null);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  return {
    status,
    result,
    error,
    startPolling,
    stopPolling,
    resetJob,
    isTerminal: status ? TERMINAL_STATES.has(status.status) : false,
  };
}

export default useAnalysisJob;
